import type {
  HelpChatBaseResponse,
  HelpChatFileDownload,
  HelpChatFileUploadStatus,
  HelpChatHistoryByTicket,
  HelpChatHistoryPage,
  HelpChatIncomingByTicket,
  HelpChatManagerStatus,
  HelpChatUploadedFile,
} from "~/types/helpChat";

type RequestOptions = {
  query?: Record<string, string | number | boolean | undefined>;
  body?: Record<string, any> | FormData;
};

const isBaseResponse = <T>(
  value: unknown,
): value is HelpChatBaseResponse<T> => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return "error" in candidate || "msg" in candidate || "data" in candidate;
};

const buildTargetBaseUrl = (rawUrl?: string) => {
  const normalized = String(rawUrl ?? "")
    .trim()
    .replace(/\/+$/, "");
  if (!normalized) return "";
  return normalized.endsWith("/v1") ? normalized : `${normalized}/v1`;
};

export const useHelpChatApi = () => {
  const runtimeConfig = useRuntimeConfig();
  const baseUrl = buildTargetBaseUrl(runtimeConfig.public.helpChatApiUrl);

  const request = async <T>(
    method: "GET" | "POST" | "PATCH" | "DELETE",
    path: string,
    options: RequestOptions = {},
  ) => {
    if (!baseUrl) {
      throw new Error("NUXT_PUBLIC_HELP_CHAT_API_URL is not configured");
    }

    const normalizedPath = path.replace(/^\/+/, "");
    const response = await $fetch<HelpChatBaseResponse<T> | T>(
      `${baseUrl}/${normalizedPath}`,
      {
        method,
        query: options.query,
        body: options.body,
        credentials: "include",
        headers: {
          "X-Auth-Source": "oneass",
        },
      },
    );

    if (isBaseResponse<T>(response)) {
      if (response.error) {
        throw new Error(response.msg || "Ошибка API чата");
      }
      return response.data as T;
    }

    return response as T;
  };

  const getClientHistory = (page = 1, limit = 50) =>
    request<HelpChatHistoryPage>("GET", "ticket/client/history", {
      query: { page, limit },
    });

  const getNewMessages = () =>
    request<HelpChatIncomingByTicket>("GET", "ticket/new");

  const sendClientText = (text: string) =>
    request<number>("POST", "ticket/client/send/text", { body: { text } });

  const sendClientFile = (ticketId: number, file: File) => {
    const formData = new FormData();
    formData.append("ticket_id", String(ticketId));
    formData.append("file", file);

    return request<HelpChatUploadedFile>("POST", "ticket/client/send/file", {
      body: formData,
    });
  };

  const markAsRead = (uuids: string[]) =>
    request<void>("PATCH", "ticket", { body: { uuids } });

  const getFileDownloadLink = (fileId: number) =>
    request<HelpChatFileDownload>("GET", `ticket/file/${fileId}`);

  const getFileUploadStatus = (fileId: number) =>
    request<HelpChatFileUploadStatus>("GET", `ticket/file/${fileId}/status`);

  const getClientManagerStatus = () =>
    request<HelpChatManagerStatus>("GET", "ticket/client/manager/status");

  const searchClientMessages = (
    text: string,
    dateFrom?: string,
    dateTo?: string,
  ) =>
    request<HelpChatHistoryByTicket>("POST", "ticket/client/search", {
      body: {
        text,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      },
    });

  return {
    getClientHistory,
    getNewMessages,
    sendClientText,
    sendClientFile,
    markAsRead,
    getFileDownloadLink,
    getFileUploadStatus,
    getClientManagerStatus,
    searchClientMessages,
  };
};
